import React, { useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import DOMPurify from 'dompurify';
import { Calendar as CalendarIcon, MapPin, Clock, X } from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Calendar localizer
const localizer = momentLocalizer(moment);

const MyCalendar = ({ events }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formattedEvents = events.map(event => ({
    title: event.summary,
    start: new Date(event.start.dateTime || event.start.date),
    end: new Date(event.end.dateTime || event.end.date),
    allDay: !event.start.dateTime,
    desc: event.description,
    location: event.location,
    originalEvent: event
  }));

  const openModal = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  // Custom event component
  const EventComponent = ({ event }) => (
    <div className="py-1 px-2 overflow-hidden">
      <div className="font-bold text-white text-sm truncate">{event.title}</div>
      {event.location && <div className="text-white text-xs opacity-90 truncate">{event.location}</div>}
    </div>
  );

  // Custom toolbar component
  const CustomToolbar = (toolbar) => {
    const goToBack = () => {
      toolbar.onNavigate('PREV');
    };
    
    const goToNext = () => {
      toolbar.onNavigate('NEXT');
    };
    
    const goToCurrent = () => {
      toolbar.onNavigate('TODAY');
    };

    const label = () => {
      const date = moment(toolbar.date);
      return (
        <span className="font-display text-h3 text-secondary">{date.format('MMMM YYYY')}</span>
      );
    };

    return (
      <div className="flex flex-wrap items-center justify-between mb-4 p-2">
        <div className="flex space-x-2 mb-2 sm:mb-0">
          <button
            onClick={goToBack}
            className="bg-lightTeal hover:bg-primary hover:text-white text-secondary font-medium py-2 px-4 rounded-lg transition-colors"
          >
            &#8592; Prev
          </button>
          <button
            onClick={goToNext}
            className="bg-lightTeal hover:bg-primary hover:text-white text-secondary font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Next &#8594;
          </button>
        </div>
        
        <div className="mx-4 text-center flex-grow">
          {label()}
        </div>
        
        <button
          onClick={goToCurrent}
          className="bg-primary text-white font-medium py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors"
        >
          Today
        </button>
      </div>
    );
  };

  // Event Modal Component
  const EventModal = ({ event, isOpen, onClose }) => {
    if (!event) return null;

    const eventStartDate = new Date(event.start);
    const eventEndDate = new Date(event.end);
    const formattedDate = eventStartDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    const formattedTime = !event.allDay ? 
      `${eventStartDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} - 
       ${eventEndDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}` : 
      "All Day";

    const addBlankTarget = (htmlString) => {
      if (!htmlString) return "No additional details available at the moment.";
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      const links = doc.querySelectorAll('a');

      links.forEach(link => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        // Add styling to links
        link.style.color = '#FF9B71'; // coral color
        link.style.textDecoration = 'underline';
      });

      return doc.body.innerHTML;
    };

    const eventDescription = event.desc 
      ? addBlankTarget(event.desc) 
      : "No additional details available at the moment.";

    const cleanDescription = DOMPurify.sanitize(eventDescription);

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          >
            <motion.div 
              className="bg-white rounded-xl shadow-lg overflow-hidden max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-primary p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4">
                    <CalendarIcon className="text-primary" size={24} />
                  </div>
                  <h3 className="font-display text-h3 text-white">Event Details</h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-6 md:p-8">
                <h2 className="font-display text-h2 text-secondary mb-6">{event.title}</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <CalendarIcon size={20} className="text-primary mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-textPrimary font-bold">Date</p>
                      <p>{formattedDate}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Clock size={20} className="text-primary mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-textPrimary font-bold">Time</p>
                      <p>{formattedTime}</p>
                    </div>
                  </div>
                  
                  {event.location && (
                    <div className="flex items-start">
                      <MapPin size={20} className="text-primary mr-3 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-textPrimary font-bold">Location</p>
                        <p>{event.location}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="w-full h-px bg-lightTeal my-6"></div>
                
                <div>
                  <h4 className="font-display text-lg text-secondary mb-3">Event Details</h4>
                  <div 
                    className="text-textPrimary prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: cleanDescription }}
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <>
      <motion.div 
        className="bg-white rounded-xl shadow-lg p-6 overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <Calendar
          localizer={localizer}
          events={formattedEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '80vh' }}
          views={['month', 'week', 'day', 'agenda']}
          components={{
            event: EventComponent,
            toolbar: CustomToolbar
          }}
          eventPropGetter={() => ({
            style: {
              backgroundColor: '#2B5B6B', // secondary color
              borderRadius: '4px',
              border: 'none',
              color: 'white'
            }
          })}
          dayPropGetter={(date) => {
            const today = new Date();
            return {
              style: {
                backgroundColor: date.getDate() === today.getDate() && 
                              date.getMonth() === today.getMonth() && 
                              date.getFullYear() === today.getFullYear() 
                  ? '#E8F3F4' // light teal for today
                  : undefined
              }
            }
          }}
          onSelectEvent={openModal}
          messages={{
            noEventsInRange: 'No events scheduled for this time period'
          }}
        />
        
        {/* CSS Styles for Calendar */}
        <style jsx global>{`
          .rbc-header {
            background-color: #2B5B6B;
            color: white;
            padding: 10px;
            font-family: 'Lato', sans-serif;
            font-weight: bold;
          }
          .rbc-toolbar button {
            font-family: 'Lato', sans-serif;
          }
          .rbc-event {
            border-radius: 4px;
          }
          .rbc-today {
            background-color: #E8F3F4;
          }
          .rbc-off-range-bg {
            background-color: #f5f7f7;
          }
          .rbc-toolbar-label {
            font-family: 'DM Serif Display', serif;
            font-size: 1.5rem;
            color: #2B5B6B;
          }
          .rbc-month-view, .rbc-time-view, .rbc-agenda-view {
            border-radius: 0.5rem;
            overflow: hidden;
            border: 1px solid #E8F3F4;
          }
          .rbc-day-bg + .rbc-day-bg, .rbc-month-row + .rbc-month-row {
            border-color: #E8F3F4;
          }
          .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
            padding: 10px;
          }
        `}</style>
      </motion.div>

      {/* Event Modal */}
      <EventModal 
        event={selectedEvent} 
        isOpen={isModalOpen} 
        onClose={closeModal} 
      />
    </>
  );
};

export default MyCalendar;